/**
 * Маршруты.
 *
 * Состояние экрана живёт в адресе: фильтр, выбранный проект и поисковый запрос
 * — параметры маршрута. Ссылку на «просроченные задачи проекта Миграция ЦОД»
 * можно отправить коллеге, и она откроется тем же экраном.
 */

import * as React from "react";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";

import { AppShell } from "@/app/AppShell";
import { ErrorBoundary } from "@/app/ErrorBoundary";
import { chunk } from "@/shared/lib/chunk";
import { SETTINGS_SECTIONS, type SettingsSection } from "@/modules/admin/sections";
import type { CalendarScale } from "@/modules/views/CalendarView";
import type { GanttScale } from "@/modules/views/GanttView";
import { TasksPage } from "@/modules/tasks/TasksPage";
import { SkeletonRows } from "@/shared/ui/Feedback";
import type { TaskFilters } from "@/shared/api/tasks";

/**
 * Разделы грузятся по мере надобности.
 *
 * Список задач нужен сразу — он открывается по умолчанию, и ждать его загрузку
 * человек не должен. Всё остальное приезжает вместе с переходом: диаграмма
 * Гантта, графики отчётов и редактор текста весят больше самого приложения,
 * а открывают их не каждый день.
 */
const TaskCard = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/tasks/TaskCard")).TaskCard })),
);
const PlannerPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/views/PlannerPage")).PlannerPage })),
);
const CalendarPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/views/CalendarPage")).CalendarPage })),
);
const GanttPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/views/GanttPage")).GanttPage })),
);
const ReportsPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/analytics/ReportsPage")).ReportsPage })),
);
const ContactsPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/contacts/ContactsPage")).ContactsPage })),
);
const ProjectOverview = React.lazy(() =>
  chunk(async () => ({
    default: (await import("@/modules/projects/ProjectOverview")).ProjectOverview,
  })),
);
const SettingsPage = React.lazy(() =>
  chunk(async () => ({ default: (await import("@/modules/admin/SettingsPage")).SettingsPage })),
);

export interface TaskSearch {
  /** Открытое представление: ссылка открывает у коллеги то же состояние. */
  view?: number;
  project?: number;
  status?: number[];
  /** Исполнители: «Мои задачи» — это отбор по себе, и ссылкой он передаётся. */
  assignee?: number[];
  q?: string;
  overdue?: boolean;
  favorite?: boolean;
  unread?: boolean;
  trash?: boolean;
}

function toFilters(search: TaskSearch): TaskFilters {
  const filters: TaskFilters = {};
  if (search.project !== undefined) {
    filters.project_id = search.project;
  }
  if (search.status && search.status.length > 0) {
    filters.status_id = search.status;
  }
  if (search.assignee && search.assignee.length > 0) {
    filters.assignee_id = search.assignee;
  }
  if (search.q) {
    filters.q = search.q;
  }
  for (const key of ["overdue", "favorite", "unread", "trash"] as const) {
    if (search[key]) {
      filters[key] = true;
    }
  }
  return filters;
}

function toSearch(filters: TaskFilters): TaskSearch {
  const search: TaskSearch = {};
  if (filters.project_id) {
    search.project = filters.project_id;
  }
  if (filters.status_id && filters.status_id.length > 0) {
    search.status = filters.status_id;
  }
  if (filters.assignee_id && filters.assignee_id.length > 0) {
    search.assignee = filters.assignee_id;
  }
  if (filters.q) {
    search.q = filters.q;
  }
  for (const key of ["overdue", "favorite", "unread", "trash"] as const) {
    if (filters[key]) {
      search[key] = true;
    }
  }
  return search;
}

const rootRoute = createRootRoute({
  component: function Root(): React.ReactElement {
    return (
      <AppShell>
        <ErrorBoundary area="Содержимое раздела">
          {/* Пока раздел подгружается, на экране скелет, а не пустота */}
          <React.Suspense fallback={<SkeletonRows count={8} />}>
            <Outlet />
          </React.Suspense>
        </ErrorBoundary>
      </AppShell>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    // Так устроен контракт роутера: перенаправление выбрасывается, а не возвращается
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/tasks" });
  },
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  validateSearch: (search: Record<string, unknown>): TaskSearch => ({
    ...(typeof search["view"] === "number" ? { view: search["view"] } : {}),
    ...(typeof search["project"] === "number" ? { project: search["project"] } : {}),
    ...(Array.isArray(search["assignee"])
      ? { assignee: (search["assignee"] as unknown[]).map(Number) }
      : {}),
    ...(Array.isArray(search["status"])
      ? { status: (search["status"] as unknown[]).map(Number) }
      : {}),
    ...(typeof search["q"] === "string" ? { q: search["q"] } : {}),
    ...(search["overdue"] ? { overdue: true } : {}),
    ...(search["favorite"] ? { favorite: true } : {}),
    ...(search["unread"] ? { unread: true } : {}),
    ...(search["trash"] ? { trash: true } : {}),
  }),
  component: function TasksScreen(): React.ReactElement {
    const search = useSearch({ from: tasksRoute.id });
    const navigate = useNavigate();

    return (
      <TasksPage
        filters={toFilters(search)}
        {...(search.view === undefined ? {} : { viewId: search.view })}
        onViewChange={(viewId) =>
          void navigate({
            to: "/tasks",
            search: viewId === null ? {} : { view: viewId },
          })
        }
        onFiltersChange={(filters) =>
          void navigate({
            to: "/tasks",
            search: {
              ...(search.view === undefined ? {} : { view: search.view }),
              ...toSearch(filters),
            },
          })
        }
        onOpenTask={(taskId) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
        }
      />
    );
  },
});

const taskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId",
  component: function TaskScreen(): React.ReactElement {
    const { taskId } = useParams({ from: taskRoute.id });
    const navigate = useNavigate();

    return (
      <TaskCard
        taskId={Number(taskId)}
        onOpenTask={(next) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(next) } })
        }
      />
    );
  },
});

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  component: function ProjectScreen(): React.ReactElement {
    const { projectId } = useParams({ from: projectRoute.id });
    const navigate = useNavigate();
    const id = Number(projectId);

    return (
      <ProjectOverview
        projectId={id}
        onOpenTask={(taskId) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
        }
        onOpenTasks={(filter) =>
          void navigate({
            to: "/tasks",
            search: {
              project: id,
              ...(filter.statusId === undefined ? {} : { status: [filter.statusId] }),
              ...(filter.overdue ? { overdue: true } : {}),
            },
          })
        }
      />
    );
  },
});

const plannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planner",
  // Планировщик живёт в адресе: ссылку на «мой планировщик» можно прислать
  validateSearch: (search: Record<string, unknown>): { view?: number } =>
    typeof search["view"] === "number" ? { view: search["view"] } : {},
  component: function PlannerScreen(): React.ReactElement {
    const search = useSearch({ from: plannerRoute.id });
    const navigate = useNavigate();

    return (
      <PlannerPage
        {...(search.view === undefined ? {} : { plannerId: search.view })}
        onPlannerChange={(viewId) => void navigate({ to: "/planner", search: { view: viewId } })}
        onOpenTask={(taskId) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
        }
      />
    );
  },
});

interface CalendarSearch {
  view?: number;
  scale?: CalendarScale;
  hourly?: boolean;
}

const SCALES: CalendarScale[] = ["day", "week", "month"];

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  // Масштаб и почасовая сетка живут в адресе: ссылка открывает то же самое
  validateSearch: (search: Record<string, unknown>): CalendarSearch => ({
    ...(typeof search["view"] === "number" ? { view: search["view"] } : {}),
    ...(SCALES.includes(search["scale"] as CalendarScale)
      ? { scale: search["scale"] as CalendarScale }
      : {}),
    ...(search["hourly"] ? { hourly: true } : {}),
  }),
  component: function CalendarScreen(): React.ReactElement {
    const search = useSearch({ from: calendarRoute.id });
    const navigate = useNavigate();
    const go = (next: CalendarSearch): void => {
      void navigate({ to: "/calendar", search: next });
    };

    return (
      <CalendarPage
        {...(search.view === undefined ? {} : { viewId: search.view })}
        scale={search.scale ?? "week"}
        hourly={search.hourly ?? false}
        onViewChange={(viewId) =>
          go({ ...search, ...(viewId === null ? { view: undefined } : { view: viewId }) })
        }
        onScaleChange={(scale) => go({ ...search, scale })}
        onHourlyChange={(hourly) => go({ ...search, hourly })}
        onOpenTask={(taskId) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
        }
      />
    );
  },
});

const GANTT_SCALES: GanttScale[] = ["day", "week", "month", "quarter"];

const ganttRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gantt",
  validateSearch: (search: Record<string, unknown>): { view?: number; scale?: GanttScale } => ({
    ...(typeof search["view"] === "number" ? { view: search["view"] } : {}),
    ...(GANTT_SCALES.includes(search["scale"] as GanttScale)
      ? { scale: search["scale"] as GanttScale }
      : {}),
  }),
  component: function GanttScreen(): React.ReactElement {
    const search = useSearch({ from: ganttRoute.id });
    const navigate = useNavigate();

    return (
      <GanttPage
        {...(search.view === undefined ? {} : { viewId: search.view })}
        scale={search.scale ?? "week"}
        onViewChange={(viewId) =>
          void navigate({
            to: "/gantt",
            search: { ...search, ...(viewId === null ? { view: undefined } : { view: viewId }) },
          })
        }
        onScaleChange={(scale) => void navigate({ to: "/gantt", search: { ...search, scale } })}
        onOpenTask={(taskId) =>
          void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
        }
      />
    );
  },
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  // Открытый отчёт живёт в адресе: ссылку на «просроченные по проектам» шлют коллеге
  validateSearch: (search: Record<string, unknown>): { report?: number } =>
    typeof search["report"] === "number" ? { report: search["report"] } : {},
  component: function ReportsScreen(): React.ReactElement {
    const search = useSearch({ from: reportsRoute.id });
    const navigate = useNavigate();

    return (
      <ReportsPage
        {...(search.report === undefined ? {} : { reportId: search.report })}
        onReportChange={(reportId) =>
          void navigate({
            to: "/reports",
            search: reportId === null ? {} : { report: reportId },
          })
        }
      />
    );
  },
});

const contactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contacts",
  // Открытый контакт живёт в адресе: ссылку на карточку клиента шлют коллеге
  validateSearch: (search: Record<string, unknown>): { contact?: number } =>
    typeof search["contact"] === "number" ? { contact: search["contact"] } : {},
  component: function ContactsScreen(): React.ReactElement {
    const search = useSearch({ from: contactsRoute.id });
    const navigate = useNavigate();

    return (
      <ContactsPage
        {...(search.contact === undefined ? {} : { contactId: search.contact })}
        onSelect={(contactId) =>
          void navigate({
            to: "/contacts",
            search: contactId === null ? {} : { contact: contactId },
          })
        }
      />
    );
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  // Раздел настройки живёт в адресе: ссылку на конструктор полей можно прислать
  validateSearch: (search: Record<string, unknown>): { section?: SettingsSection } => {
    const value = search["section"];
    return typeof value === "string" && SETTINGS_SECTIONS.includes(value as SettingsSection)
      ? { section: value as SettingsSection }
      : {};
  },
  component: function SettingsScreen(): React.ReactElement {
    const search = useSearch({ from: settingsRoute.id });
    const navigate = useNavigate();

    return (
      <SettingsPage
        {...(search.section ? { section: search.section } : {})}
        onSectionChange={(section) => void navigate({ to: "/settings", search: { section } })}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  tasksRoute,
  taskRoute,
  projectRoute,
  plannerRoute,
  calendarRoute,
  ganttRoute,
  reportsRoute,
  contactsRoute,
  settingsRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
